import React, { FC, forwardRef, useImperativeHandle, useMemo } from 'react'

import { MasterCampaignPlan } from 'api/interfaces/MasterCampaignPlan'
import { MasterCampaignPlanCampaignBuilder, MasterCampaignStub, MasterCampaignPlanCampaignBuilderType } from 'api/interfaces/MasterCampaignStub'

import BillboardLocations, { BillboardLocationsRef } from './BillboardLocations'
import TargetedHouseholdLocations, { TargetedHouseholdLocationsRef } from './TargetedHouseholdLocations'

export interface CampaignSpecificTargetedHouseholdMapProps {
  masterCampaignPlan: MasterCampaignPlan
  campaign: MasterCampaignPlanCampaignBuilder
  isCapturing?: boolean
}

export interface CampaignSpecificTargetedHouseholdMapRef {
  getMapCanvas: () => Promise<string | null>
  isMapReady: () => boolean
}

export interface CampaignSpecificBillboardMapProps {
  masterCampaignStub: MasterCampaignStub
  campaign: MasterCampaignPlanCampaignBuilder
  isCapturing?: boolean
}

export interface CampaignSpecificBillboardMapRef {
  getMapCanvas: () => Promise<string | null>
  isMapReady: () => boolean
}

const CampaignSpecificTargetedHouseholdMap = forwardRef<CampaignSpecificTargetedHouseholdMapRef, CampaignSpecificTargetedHouseholdMapProps>(({ masterCampaignPlan, campaign, isCapturing }, ref) => {
  const targetedHouseholdMapRef = React.useRef<TargetedHouseholdLocationsRef>(null)

  useImperativeHandle(
    ref,
    () => ({
      getMapCanvas: async () => {
        if (!targetedHouseholdMapRef.current) {
          return null
        }
        return await targetedHouseholdMapRef.current.getMapCanvas()
      },
      isMapReady: () => {
        if (!targetedHouseholdMapRef.current) {
          return false
        }
        return targetedHouseholdMapRef.current.isMapReady()
      }
    }),
    []
  )

  const isInternet = campaign.campaignType === MasterCampaignPlanCampaignBuilderType.Internet
  const isTV = campaign.campaignType === MasterCampaignPlanCampaignBuilderType.TV
  const isOffAdCritterWithProjectionsAndGeoHeatmap = campaign.campaignType === MasterCampaignPlanCampaignBuilderType.OffAdCritter && campaign.includeInProjections && campaign.includeInGeoHeatmap

  if (!isInternet && !isTV && !isOffAdCritterWithProjectionsAndGeoHeatmap) {
    return null
  }

  return <TargetedHouseholdLocations ref={targetedHouseholdMapRef} masterCampaignPlan={masterCampaignPlan} isCapturing={isCapturing} specificCampaignId={campaign.id} />
})

const CampaignSpecificBillboardMap = forwardRef<CampaignSpecificBillboardMapRef, CampaignSpecificBillboardMapProps>(({ masterCampaignStub, campaign, isCapturing }, ref) => {
  const singleCampaignStub = useMemo(() => {
    return {
      ...masterCampaignStub,
      campaignBuilders: [campaign]
    }
  }, [masterCampaignStub, campaign])

  const billboardMapRef = React.useRef<BillboardLocationsRef>(null)

  useImperativeHandle(
    ref,
    () => ({
      getMapCanvas: async () => {
        if (!billboardMapRef.current) {
          return null
        }
        return await billboardMapRef.current.getMapCanvas()
      },
      isMapReady: () => {
        if (!billboardMapRef.current) {
          return false
        }
        return billboardMapRef.current.isMapReady()
      }
    }),
    []
  )

  if (campaign.campaignType !== MasterCampaignPlanCampaignBuilderType.Billboard) {
    return null
  }

  return <BillboardLocations ref={billboardMapRef} masterCampaignStub={singleCampaignStub} isCapturing={isCapturing} />
})

CampaignSpecificTargetedHouseholdMap.displayName = 'CampaignSpecificTargetedHouseholdMap'
CampaignSpecificBillboardMap.displayName = 'CampaignSpecificBillboardMap'

export { CampaignSpecificTargetedHouseholdMap, CampaignSpecificBillboardMap }
